import { useState } from "react";
import { motion } from "framer-motion";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Layout from "../components/Layout";
import { toast } from "sonner";
import { Check, Crown, Loader2 } from "lucide-react";

const PLANS = [
  {
    id: "free", name: "Безплатен", price: "0", lookup: null, color: "slate",
    features: ["5 AI заявки на месец", "Достъп до библиотеката", "Изтегляне като .pptx", "1 потребител"],
  },
  {
    id: "pro", name: "Pro", price: "9.99", lookup: "pro_monthly", color: "sky", popular: true,
    features: ["Неограничени AI презентации", "Неограничени работни листове и планове", "Премиум материали", "Публикуване и споделяне", "Приоритетна поддръжка"],
  },
  {
    id: "school", name: "Училище", price: "49.99", lookup: "school_monthly", color: "amber",
    features: ["Всичко от Pro", "До 20 учителски профила", "Споделена училищна библиотека", "Административен панел", "Обучение за екипа"],
  },
];

export default function Pricing() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(null);

  const subscribe = async (lookup) => {
    if (!lookup) return;
    setLoading(lookup);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key: lookup, origin_url: window.location.origin });
      // Stripe Checkout cannot render inside an iframe (X-Frame-Options),
      // so break out to the top-level window when the app is embedded (e.g. preview).
      const url = data.checkout_url;
      try {
        if (window.top && window.top !== window.self) {
          window.top.location.href = url;
        } else {
          window.location.href = url;
        }
      } catch {
        window.open(url, "_blank");
      }
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
      setLoading(null);
    }
  };

  return (
    <Layout>
      <div className="max-w-5xl">
        <div className="text-center max-w-xl mx-auto">
          <h1 className="font-heading text-3xl sm:text-4xl font-black text-slate-900">Изберете своя план</h1>
          <p className="text-slate-500 mt-2">Спестете часове подготовка. Отменяне по всяко време.</p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mt-10">
          {PLANS.map((p, i) => {
            const current = user?.plan === p.id;
            return (
              <motion.div key={p.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 * i }}
                data-testid={`plan-${p.id}`}
                className={`relative bg-white rounded-3xl p-8 shadow-sm border-2 ${p.popular ? "border-sky-400 shadow-lg" : "border-slate-100"}`}>
                {p.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-sky-500 text-white text-xs font-bold px-4 py-1 rounded-full flex items-center gap-1">
                    <Crown size={12} /> Най-популярен
                  </div>
                )}
                <h3 className="font-heading font-black text-2xl text-slate-900">{p.name}</h3>
                <div className="mt-3 mb-6">
                  <span className="font-heading text-4xl font-black text-slate-900">{p.price}</span>
                  <span className="text-slate-400 font-semibold"> €/мес.</span>
                </div>
                <ul className="space-y-3 mb-8">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-slate-600">
                      <Check size={18} className="text-emerald-500 shrink-0 mt-0.5" /> {f}
                    </li>
                  ))}
                </ul>
                <button
                  data-testid={`subscribe-${p.id}`}
                  disabled={current || !p.lookup || loading === p.lookup}
                  onClick={() => subscribe(p.lookup)}
                  className={`w-full py-3 rounded-full font-bold transition-colors flex items-center justify-center gap-2 disabled:opacity-60 ${
                    p.popular ? "bg-sky-500 hover:bg-sky-600 text-white" : "bg-slate-900 hover:bg-slate-800 text-white"
                  } ${current ? "!bg-emerald-100 !text-emerald-700" : ""}`}>
                  {loading === p.lookup ? <Loader2 size={18} className="animate-spin" /> :
                    current ? "Активен план" : p.lookup ? "Абонирай се" : "Безплатен"}
                </button>
              </motion.div>
            );
          })}
        </div>
        <p className="text-center text-xs text-slate-400 mt-8">
          Тестова карта: 4242 4242 4242 4242 · произволна бъдеща дата · произволен CVC
        </p>
      </div>
    </Layout>
  );
}
